import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Arabic numerals mapping
const ARABIC_NUMERALS: Record<string, string> = {
  '0': '٠', '1': '١', '2': '٢', '3': '٣', '4': '٤',
  '5': '٥', '6': '٦', '7': '٧', '8': '٨', '9': '٩',
};

const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];

const ARABIC_DAYS = [
  'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'
];

const ENGLISH_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ENGLISH_MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Convert Western numerals to Arabic numerals
 */
export function toArabicNumerals(num: number | string): string {
  return String(num).replace(/\d/g, (d) => ARABIC_NUMERALS[d] || d);
}

/**
 * Format a number based on locale
 */
export function formatNumber(num: number, lang: string, useArabicNumerals = false): string {
  const formatted = new Intl.NumberFormat(lang === 'ar' ? 'ar-SA' : 'en-US').format(num);
  if (lang === 'ar' && useArabicNumerals) {
    return toArabicNumerals(formatted);
  }
  return formatted;
}

/**
 * Format a date for display
 */
export function formatDate(
  dateStr: string | Date,
  lang: string = 'en',
  options: { format?: 'short' | 'long' | 'relative'; useArabicNumerals?: boolean } = {}
): string {
  const { format = 'short', useArabicNumerals = false } = options;
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Relative format
  if (format === 'relative') {
    if (diffDays === 0) {
      return lang === 'ar' ? 'اليوم' : 'Today';
    } else if (diffDays === 1) {
      return lang === 'ar' ? 'أمس' : 'Yesterday';
    } else if (diffDays === -1) {
      return lang === 'ar' ? 'غداً' : 'Tomorrow';
    } else if (diffDays > 0 && diffDays < 7) {
      return lang === 'ar' 
        ? `منذ ${useArabicNumerals ? toArabicNumerals(diffDays) : diffDays} أيام`
        : `${diffDays} days ago`;
    } else if (diffDays < 0 && diffDays > -7) {
      const absDays = Math.abs(diffDays);
      return lang === 'ar'
        ? `خلال ${useArabicNumerals ? toArabicNumerals(absDays) : absDays} أيام`
        : `In ${absDays} days`;
    }
  }

  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  if (lang === 'ar') {
    const dayStr = useArabicNumerals ? toArabicNumerals(day) : day;
    const yearStr = useArabicNumerals ? toArabicNumerals(year) : year;
    
    if (format === 'long') {
      return `${dayStr} ${ARABIC_MONTHS[month]} ${yearStr}`;
    }
    return `${dayStr} ${ARABIC_MONTHS[month].slice(0, 3)}`;
  }

  if (format === 'long') {
    return `${ENGLISH_MONTHS[month]} ${day}, ${year}`;
  }
  return `${ENGLISH_MONTHS_SHORT[month]} ${day}`;
}

/**
 * Format time for display
 */
export function formatTime(
  dateStr: string | Date,
  lang: string = 'en',
  options: { use24Hour?: boolean; useArabicNumerals?: boolean } = {}
): string {
  const { use24Hour = false, useArabicNumerals = false } = options;
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  
  if (isNaN(date.getTime())) return '';

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const minutesStr = minutes.toString().padStart(2, '0');

  if (use24Hour) {
    const hoursStr = hours.toString().padStart(2, '0');
    const time = `${hoursStr}:${minutesStr}`;
    return lang === 'ar' && useArabicNumerals ? toArabicNumerals(time) : time;
  }

  const period = hours >= 12 
    ? (lang === 'ar' ? 'م' : 'PM') 
    : (lang === 'ar' ? 'ص' : 'AM');
  hours = hours % 12 || 12;
  const time = `${hours}:${minutesStr}`;
  
  if (lang === 'ar') {
    return useArabicNumerals 
      ? `${toArabicNumerals(time)} ${period}`
      : `${time} ${period}`;
  }
  return `${time} ${period}`;
}

/**
 * Format date and time together
 */
export function formatDateTime(
  dateStr: string | Date,
  lang: string = 'en',
  options: { useArabicNumerals?: boolean } = {}
): string {
  const date = formatDate(dateStr, lang, { format: 'short', ...options });
  const time = formatTime(dateStr, lang, options);
  
  if (lang === 'ar') {
    return `${date}، ${time}`;
  }
  return `${date}, ${time}`;
}

/**
 * Parse natural language dates (Arabic & English)
 */
export function parseNaturalDate(text: string, baseLang: string = 'en'): Date | null {
  const now = new Date();
  const lowerText = text.toLowerCase().trim();

  // English patterns
  const englishPatterns: Record<string, () => Date> = {
    'today': () => now,
    'tomorrow': () => new Date(now.getTime() + 24 * 60 * 60 * 1000),
    'yesterday': () => new Date(now.getTime() - 24 * 60 * 60 * 1000),
    'next week': () => new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000),
    'next month': () => new Date(now.getFullYear(), now.getMonth() + 1, now.getDate()),
  };

  // Arabic patterns
  const arabicPatterns: Record<string, () => Date> = {
    'اليوم': () => now,
    'غداً': () => new Date(now.getTime() + 24 * 60 * 60 * 1000),
    'غدا': () => new Date(now.getTime() + 24 * 60 * 60 * 1000),
    'أمس': () => new Date(now.getTime() - 24 * 60 * 60 * 1000),
    'الأسبوع القادم': () => new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000),
    'الشهر القادم': () => new Date(now.getFullYear(), now.getMonth() + 1, now.getDate()),
  };

  // Check patterns
  const patterns = baseLang === 'ar' 
    ? { ...arabicPatterns, ...englishPatterns }
    : { ...englishPatterns, ...arabicPatterns };

  for (const [pattern, getDate] of Object.entries(patterns)) {
    if (lowerText.includes(pattern) || text.includes(pattern)) {
      return getDate();
    }
  }

  // Try parsing as standard date
  const parsed = new Date(text);
  if (!isNaN(parsed.getTime())) {
    return parsed;
  }

  return null;
}

/**
 * Get ordinal suffix for numbers
 */
export function getOrdinal(n: number, lang: string = 'en'): string {
  if (lang === 'ar') {
    // Arabic doesn't use ordinal suffixes the same way
    return String(n);
  }
  
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number, ellipsis: string = '...'): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - ellipsis.length) + ellipsis;
}

/**
 * Generate a slug from text (supports Arabic)
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[\s\-]+/g, '-') // Replace spaces and hyphens with single hyphen
    .replace(/[^\p{L}\p{N}\-]/gu, '') // Keep letters, numbers, and hyphens (Unicode-aware)
    .replace(/\-+/g, '-') // Collapse multiple hyphens
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Check if text contains Arabic characters
 */
export function containsArabic(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text);
}

/**
 * Get text direction based on content
 */
export function getTextDirection(text: string): 'ltr' | 'rtl' {
  // Check first significant character
  const trimmed = text.trim();
  if (!trimmed) return 'ltr';
  
  // RTL Unicode ranges
  const rtlPattern = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0590-\u05FF\uFB1D-\uFB4F]/;
  return rtlPattern.test(trimmed) ? 'rtl' : 'ltr';
}

/**
 * Format a duration in seconds to human readable
 */
export function formatDuration(seconds: number, lang: string = 'en'): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (lang === 'ar') {
    if (hours > 0) {
      return `${hours} ساعة ${minutes > 0 ? `و ${minutes} دقيقة` : ''}`;
    }
    return `${minutes} دقيقة`;
  }
  
  if (hours > 0) {
    return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
  }
  return `${minutes}m`;
}
